import { memo } from 'react';
import type { MessengerMessage } from '@/api/client';
import { UnifiedMessageList } from '@/components/Messenger/unified/UnifiedMessageList';
import type { AgentChatModelOption, AgentChatQuotaSnapshot, ChatModelId } from '@/components/Messenger/agent/agentChatSharedTypes';
import type { TrackerChatMessage } from '@/components/Messenger/types/chatMessageTypes';
import type { TeleportContextValue } from '@/hooks/useTeleportToTarget';

export type AgentMessageListProps = {
  animationContextId: string;
  historicalMessages: TrackerChatMessage[];
  status: string;
  teleport: TeleportContextValue | null;
  historyLoading: boolean;
  streamBusy: boolean;
  agentQuota: AgentChatQuotaSnapshot | null;
  chatModelId: ChatModelId;
  chatModels: AgentChatModelOption[];
  onForwardMessage: (draft: MessengerMessage) => void;
  sendQuickMessage: (m: { text: string }) => void;
  onEditMessage: (messageId: string) => void;
  onRetryUserWithModel: (messageId: string, modelId: ChatModelId) => void;
  onRetrySend: () => void;
  isModelSendBlocked: (modelId: ChatModelId) => boolean;
};

function AgentMessageListInner({
  animationContextId,
  historicalMessages,
  status,
  teleport,
  historyLoading,
  streamBusy,
  agentQuota,
  chatModelId,
  chatModels,
  onForwardMessage,
  sendQuickMessage,
  onEditMessage,
  onRetryUserWithModel,
  onRetrySend,
  isModelSendBlocked,
}: AgentMessageListProps) {
  return (
    <UnifiedMessageList
      variant="agent"
      animationContextId={animationContextId}
      messages={historicalMessages}
      status={status}
      teleport={teleport}
      historyLoading={historyLoading}
      streamBusy={streamBusy}
      agentQuota={agentQuota}
      chatModelId={chatModelId}
      chatModels={chatModels}
      onForwardMessage={onForwardMessage}
      sendQuickMessage={sendQuickMessage}
      onEditMessage={onEditMessage}
      onRetryUserWithModel={onRetryUserWithModel}
      onRetrySend={onRetrySend}
      isModelSendBlocked={isModelSendBlocked}
    />
  );
}

function areAgentMessageListPropsEqual(a: AgentMessageListProps, b: AgentMessageListProps): boolean {
  return (
    a.animationContextId === b.animationContextId &&
    a.historicalMessages === b.historicalMessages &&
    a.status === b.status &&
    a.teleport === b.teleport &&
    a.historyLoading === b.historyLoading &&
    a.streamBusy === b.streamBusy &&
    a.agentQuota === b.agentQuota &&
    a.chatModelId === b.chatModelId &&
    a.chatModels === b.chatModels &&
    a.onForwardMessage === b.onForwardMessage &&
    a.sendQuickMessage === b.sendQuickMessage &&
    a.onEditMessage === b.onEditMessage &&
    a.onRetryUserWithModel === b.onRetryUserWithModel &&
    a.onRetrySend === b.onRetrySend &&
    a.isModelSendBlocked === b.isModelSendBlocked
  );
}

export const AgentMessageList = memo(AgentMessageListInner, areAgentMessageListPropsEqual);
